import {Component, computed, effect, inject, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {Title} from '../../../../components/title/title';
import {FormArray, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {CreateInvoiceRequest, InvoiceLineEdition, InvoiceService, UpdateInvoiceRequest} from '../../../../../api';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {CurrentUserService} from '../../../../services/current-user.service';
import {ActivatedRoute, Router} from '@angular/router';
import {InvoiceStore} from '../../services/invoice-store';
import {scrollToFirstError} from '../../../../utils/form.utils';
import {StepperModule} from 'primeng/stepper';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {RouteUri} from '../../../../constants/route-uri';
import {InvoiceEdit} from '../invoice-edit/invoice-edit';
import {InvoicePreview} from '../invoice-preview/invoice-preview';
import {InvoiceSend} from '../invoice-send/invoice-send';

@Component({
  selector: 'elcia-invoice-form',
  imports: [
    Title,
    TranslatePipe,
    ElciaButton,
    StepperModule,
    InvoiceEdit,
    InvoicePreview,
    InvoiceSend,
  ],
  templateUrl: './invoice-form.html',
  styleUrl: './invoice-form.scss'
})

export class InvoiceForm {

  private readonly invoiceStore = inject(InvoiceStore);
  private readonly invoiceService = inject(InvoiceService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly currentUser = computed(() => this.currentUserService.currentUser());

  private readonly routeId = this.route.snapshot.paramMap.has('id')
    ? Number(this.route.snapshot.paramMap.get('id'))
    : null;

  protected readonly currentStep = signal<number>(history.state?.step ?? 1);

  protected readonly fg = new FormGroup<InvoiceModelForm>({
    id: new FormControl(this.routeId),
    legalEntityId: new FormControl(this.currentUser()?.legalEntityId ?? null, Validators.required),
    customerId: new FormControl(null, Validators.required),
    invoiceDate: new FormControl(new Date(), Validators.required),
    invoiceLines: new FormArray<FormGroup<InvoiceLineModelForm>>([]),
    note: new FormControl('', {nonNullable: true}),
    localeIsoCode: new FormControl<string | null>(null),
  });

  private readonly legalEntityId = toSignal(this.fg.controls.legalEntityId.valueChanges, {
    initialValue: this.fg.controls.legalEntityId.value,
  });

  private readonly invoiceQuery = injectQuery(() => ({
    queryKey: ['invoice', this.routeId],
    queryFn: () => lastValueFrom(this.invoiceService.getInvoiceById(this.routeId!)),
    enabled: this.routeId != null,
  }));

  private readonly lastNumberedDateQuery = injectQuery(() => ({
    queryKey: ['invoice-last-numbered-date', this.legalEntityId()],
    queryFn: () => lastValueFrom(this.invoiceService.getLastNumberedInvoiceDate(this.legalEntityId()!)),
    enabled: this.legalEntityId() != null,
  }));

  protected readonly minInvoiceDate = computed(() => {
    const d = this.lastNumberedDateQuery.data()?.lastNumberedDate;
    return d ? new Date(d) : null;
  });

  private populated = false;
  // Le formulaire se recharge à chaque nouvelle version de la facture (une modification de produit
  // ou de produit client resynchronise les brouillons côté back), sauf si l'utilisateur a commencé
  // à la modifier : ses saisies en cours ne doivent jamais être écrasées.
  private readonly populateForm = effect(() => {
    const detail = this.invoiceQuery.data()?.invoice;
    if (!detail || (this.populated && this.fg.dirty)) return;
    this.populated = true;

    this.fg.patchValue({
      id: detail.id,
      legalEntityId: detail.legalEntityId,
      customerId: detail.customerId,
      invoiceDate: new Date(detail.invoiceDate),
      note: detail.note ?? undefined,
      localeIsoCode: detail.localeIsoCode ?? null,
    });

    this.fg.controls.invoiceLines.clear();
    detail.invoiceLines.forEach(line => this.fg.controls.invoiceLines.push(this.createInvoiceLineForm(line)));
  });

  private readonly onCurrentUserChange = effect(() => {
    if (this.currentUser() && this.currentUser()?.legalEntityId && this.fg.controls.legalEntityId.value === null)
      this.fg.controls.legalEntityId.setValue(this.currentUser()?.legalEntityId ?? null);
  });

  // Re-runs the floor validator on every floor change and, for a new invoice only, bumps the date up to the floor.
  private readonly onMinInvoiceDateChange = effect(() => {
    const min = this.minInvoiceDate();
    const control = this.fg.controls.invoiceDate;

    if (this.routeId == null && min && control.value && control.value < min) {
      control.setValue(min, {emitEvent: false});
    }

    control.updateValueAndValidity({emitEvent: false});
  });

  constructor() {
    this.fg.controls.invoiceDate.addValidators(minDateValidator(() => this.minInvoiceDate()));
  }

  protected goToPreview() {
    if (this.fg.invalid) {
      this.fg.markAllAsTouched();
      scrollToFirstError();
      return;
    }

    const id = this.fg.controls.id.value;
    if (id == null) {
      this.invoiceStore.createMutation.mutate(this.buildCreateRequest(), {
        onSuccess: response => this.router.navigate([RouteUri.INVOICE_FORM, response.id], {state: {step: 2}}),
      });
    } else {
      this.invoiceStore.updateMutation.mutate({id, request: this.buildUpdateRequest()}, {
        onSuccess: () => this.currentStep.set(2),
      });
    }
  }

  protected goToStep(step: number) {
    this.currentStep.set(step);
  }

  private buildCreateRequest(): CreateInvoiceRequest {
    const value = this.fg.getRawValue();
    return {
      invoiceDate: value.invoiceDate!,
      customerId: value.customerId!,
      note: value.note || null,
      localeIsoCode: value.localeIsoCode,
      legalEntityId: value.legalEntityId!,
      invoiceLines: this.validLines().map(line => ({
        label: line.label!,
        description: line.description,
        quantity: line.quantity!,
        unitPrice: line.unitPrice ?? 0,
        taxId: line.taxId!,
        productId: line.productId!,
        timesheetIds: line.timesheetIds?.length ? line.timesheetIds : undefined,
      })),
    };
  }

  private buildUpdateRequest(): UpdateInvoiceRequest {
    const value = this.fg.getRawValue();
    return {
      invoiceDate: value.invoiceDate!,
      customerId: value.customerId!,
      note: value.note,
      localeIsoCode: value.localeIsoCode,
      legalEntityId: value.legalEntityId!,
      invoiceLines: this.validLines().map(line => ({
        id: line.id ?? 0,
        label: line.label!,
        description: line.description,
        quantity: line.quantity!,
        unitPrice: line.unitPrice ?? 0,
        taxId: line.taxId!,
        productId: line.productId!,
        timesheetIds: line.timesheetIds?.length ? line.timesheetIds : undefined,
      })),
    };
  }

  private validLines() {
    return this.fg.getRawValue().invoiceLines
      .filter(line => !!line.label && line.quantity != null && line.taxId != null);
  }

  private createInvoiceLineForm(line?: InvoiceLineEdition) {
    const productId = new FormControl<number | null>(line?.productId ?? null, Validators.required);
    // Produit non modifiable : ligne générée par la facturation automatique (échéance de contrat)
    // ou ligne de frais de déplacement adossée à une prestation.
    if (line?.customerProductBillingId != null || line?.isTravelExpense) productId.disable();

    return new FormGroup<InvoiceLineModelForm>({
      id: new FormControl(line?.id ?? null),
      productId,
      timesheetIds: new FormControl<number[]>(line?.timesheetIds ?? [], {nonNullable: true}),
      label: new FormControl(line?.label ?? null, Validators.required),
      description: new FormControl(line?.description ?? null),
      quantity: new FormControl(line?.quantity ?? 1, Validators.required),
      unitPrice: new FormControl(line?.unitPrice ?? null, Validators.required),
      taxId: new FormControl(line?.taxId ?? null, Validators.required),
      isTravelExpenses: new FormControl(line?.isTravelExpense ?? false, {nonNullable: true}),
      customerProductBillingId: new FormControl<number | null>(line?.customerProductBillingId ?? null),
      billingPeriodStart: new FormControl<Date | null>(line?.billingPeriodStart ? new Date(line.billingPeriodStart) : null),
      billingPeriodEnd: new FormControl<Date | null>(line?.billingPeriodEnd ? new Date(line.billingPeriodEnd) : null),
    });
  }
}

function minDateValidator(getMin: () => Date | null): ValidatorFn {
  return (control) => {
    const min = getMin();
    const value = control.value as Date | null;
    return min && value && value < min ? {dateBeforeLastNumbered: true} : null;
  };
}

export interface InvoiceModelForm {
  id: FormControl<number | null>;
  legalEntityId: FormControl<number | null>;

  customerId: FormControl<number | null>;
  invoiceDate: FormControl<Date | null>;
  invoiceLines: FormArray<FormGroup<InvoiceLineModelForm>>;
  note: FormControl<string>;
  localeIsoCode: FormControl<string | null>;
}

export interface InvoiceLineModelForm {
  id: FormControl<number | null>;
  productId: FormControl<number | null>;
  timesheetIds: FormControl<number[]>;
  label: FormControl<string | null>;
  description: FormControl<string | null>;
  quantity: FormControl<number | null>;
  unitPrice: FormControl<number | null>;
  taxId: FormControl<number | null>;
  // Ligne « Frais de déplacement » adossée à une prestation ; hydratée depuis le back (isTravelExpense), jamais renvoyée au back
  isTravelExpenses: FormControl<boolean>;
  // Échéance couverte par la ligne (facturation automatique) ; lecture seule, jamais renvoyé au back
  customerProductBillingId: FormControl<number | null>;
  billingPeriodStart: FormControl<Date | null>;
  billingPeriodEnd: FormControl<Date | null>;
}
